import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import VideoEditor from './components/VideoEditor';
import Login from './components/Login';
import PlanSelection from './components/PlanSelection';
import SuccessPage from './components/SuccessPage';
import AboutVisionAI from './components/AboutVisionAI'; // 🔹 Importação do componente "Sobre Nós"

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isPlanPending, setIsPlanPending] = useState(false);

    // --------------------------------------------------------
    // (1) Ao montar o App, verifica se já existe "user" no sessionStorage.
    //     Se sim, ajusta isAuthenticated/isPlanPending conforme o status do plano.
    // --------------------------------------------------------
    useEffect(() => {
        const storedUser = sessionStorage.getItem('user');
        if (storedUser) {
            const userObj = JSON.parse(storedUser);
            const planStatus = userObj.planDetails?.status;
            if (planStatus === 'active') {
                setIsAuthenticated(true);
                setIsPlanPending(false);
            } else if (planStatus === 'pending') {
                setIsPlanPending(true);
                setIsAuthenticated(false);
            }
        }
    }, []);

    // --------------------------------------------------------
    // (2) Função de login, chamada pelo <Login /> ou por <SuccessPage />
    //     'active' => define isAuthenticated=true, isPlanPending=false
    //     'pending' => define isPlanPending=true, isAuthenticated=false
    // --------------------------------------------------------
    const handleLogin = (status) => {
        if (status === 'active') {
            setIsAuthenticated(true);
            setIsPlanPending(false);
        } else if (status === 'pending') {
            setIsPlanPending(true);
            setIsAuthenticated(false);
        }
    };

    // --------------------------------------------------------
    // (3) Função de logout
    //     Reseta o estado e limpa sessionStorage.
    // --------------------------------------------------------
    const handleLogout = () => {
        setIsAuthenticated(false);
        setIsPlanPending(false);
        sessionStorage.clear();
    };

    return (
        <Router>
            <div className="App">
                <Header />

                <Routes>
                    {/* 
                      (A) Rota raiz "/":
                          - Se não estiver autenticado e não for pendente, mostra <Login />.
                          - Se for pendente, redireciona para /plan-selection.
                          - Se estiver autenticado, vai direto pra /video-editor.
                    */}
                    <Route
                        path="/"
                        element={
                            !isAuthenticated && !isPlanPending ? (
                                <Login onLogin={handleLogin} />
                            ) : isPlanPending ? (
                                <Navigate to="/plan-selection" />
                            ) : (
                                <Navigate to="/video-editor" />
                            )
                        }
                    />

                    {/* 🔹 Nova Rota "/about" - Página Sobre Nós */}
                    <Route path="/about" element={<AboutVisionAI />} />

                    {/* 
                      (B) Rota "/reset-password": mesma lógica de "/"
                          - Se não autenticado e não pendente => <Login />
                          - Se pendente => /plan-selection
                          - Se autenticado => /video-editor
                    */}
                    <Route
                        path="/reset-password"
                        element={
                            !isAuthenticated && !isPlanPending ? (
                                <Login onLogin={handleLogin} />
                            ) : isPlanPending ? (
                                <Navigate to="/plan-selection" />
                            ) : (
                                <Navigate to="/video-editor" />
                            )
                        }
                    />

                    {/* 
                      (C) Rota "/plan-selection":
                          - Acessível se isPlanPending=true OU isAuthenticated=true (usuário logado pode querer upgrade).
                          - Senão => / (login)
                    */}
                    <Route
                        path="/plan-selection"
                        element={
                            isPlanPending || isAuthenticated ? (
                                <PlanSelection
                                    onPlanSelected={() => setIsPlanPending(false)}
                                    onLogout={handleLogout}
                                />
                            ) : (
                                <Navigate to="/" />
                            )
                        }
                    />

                    {/* 
                      (D) Rota "/success":
                          - Depois de pagar no Stripe, cairá aqui com ?session_id=xxx
                          - Passamos onLogin={handleLogin} para que o SuccessPage possa forçar
                            isAuthenticated=true ou isPlanPending=false, conforme status do plano retornado.
                    */}
                    <Route
                        path="/success"
                        element={<SuccessPage onLogin={handleLogin} />}
                    />

                    {/* 
                      (E) Rota "/video-editor":
                          - Só acessa se isAuthenticated=true, senão => /
                    */}
                    <Route
                        path="/video-editor"
                        element={
                            isAuthenticated ? (
                                <VideoEditor onLogout={handleLogout} />
                            ) : (
                                <Navigate to="/" />
                            )
                        }
                    />

                    {/* 
                      (F) Catch-all "*":
                          - Se isAuthenticated => manda /video-editor
                          - Senão => /
                    */}
                    <Route
                        path="*"
                        element={
                            <Navigate to={isAuthenticated ? "/video-editor" : "/"} />
                        }
                    />
                </Routes>
            </div>
        </Router>
    );
}

export default App;
